import React from "react";
import { Zap, Wrench, Sparkles, Activity } from "lucide-react";

/**
 * Operator Changelog / Updates panel.
 *
 * Renders two stacked sections:
 *   • Active Patches  — already-shipped iterations
 *   • Upcoming Modules — roadmap surface for prospective operators
 *
 * Used in two surfaces:
 *   1. MarketingLanding (desktop, unauthed) embeds it inline so anonymous
 *      visitors can preview the velocity of the platform before signing
 *      in.
 *   2. Authed `/updates` page (mobile + desktop) — surfaced via the
 *      header [ Updates ] tab so operators can review patches and
 *      roadmap on demand.
 */

const ACTIVE_PATCHES = [
  {
    id: "iter-11-cgnat",
    label: "FIXED",
    title: "Mobile network fix",
    blurb:
      "Some mobile users were getting blocked from their free scan when they shared a network with other testers. Everyone now gets their own free scan, no matter who they share Wi-Fi or cell signal with.",
  },
  {
    id: "iter-11-ttl",
    label: "FIXED",
    title: "Faster guest scans",
    blurb:
      "Old guest sessions now clear themselves automatically every 24 hours so the app stays snappy — no more slow lookups for new visitors.",
  },
];

const UPCOMING_MODULES = [
  {
    id: "deep-search-core",
    title: "Deep Search Mode",
    blurb:
      "For tougher claims, CMH will dig past quick web results — pulling from RSS feeds, original press releases, and primary documents to build a fuller picture.",
  },
  {
    id: "lcr-mapping",
    title: "News bias tags (Left / Center / Right)",
    blurb:
      "Every source on your verdict will get a small Left, Center, or Right tag so you can see at a glance which side of the aisle the reporting leans.",
  },
  {
    id: "bot-swarm",
    title: "Bot network tracker",
    blurb:
      "We'll trace viral claims back through the accounts spreading them and flag coordinated bot swarms — so you know when a story is being artificially amplified.",
  },
];

const PatchRow = ({ patch }) => (
  <div
    data-testid={`active-patch-${patch.id}`}
    className="flex gap-3 sm:gap-4 px-4 sm:px-5 py-4 border-b border-white/[0.06] last:border-b-0"
  >
    <div className="shrink-0 mt-0.5">
      <div className="inline-flex items-center gap-1.5 border border-emerald-400/40 bg-emerald-400/[0.08] text-emerald-300 px-2 py-0.5 font-mono text-[9.5px] uppercase tracking-[0.2em] font-bold">
        <Wrench size={10} />
        {patch.label}
      </div>
    </div>
    <div className="min-w-0 flex-1">
      <p className="font-mono text-[12.5px] sm:text-[13px] uppercase tracking-[0.16em] text-[#f1f5f9] font-bold leading-snug">
        {patch.title}
      </p>
      <p className="mt-1.5 text-[13px] sm:text-[13.5px] text-[#cbd5e1] leading-relaxed">
        {patch.blurb}
      </p>
    </div>
  </div>
);

const UpcomingRow = ({ mod }) => (
  <div
    data-testid={`upcoming-module-${mod.id}`}
    className="flex gap-3 sm:gap-4 px-4 sm:px-5 py-4 border-b border-white/[0.06] last:border-b-0"
  >
    <div className="shrink-0 mt-0.5">
      <div className="inline-flex items-center gap-1.5 border border-amber-400/40 bg-amber-400/[0.08] text-amber-300 px-2 py-0.5 font-mono text-[9.5px] uppercase tracking-[0.2em] font-bold">
        <Sparkles size={10} />
        Coming
      </div>
    </div>
    <div className="min-w-0 flex-1">
      <p className="font-mono text-[12.5px] sm:text-[13px] uppercase tracking-[0.16em] text-[#f1f5f9] font-bold leading-snug">
        {mod.title}
      </p>
      <p className="mt-1.5 text-[13px] sm:text-[13.5px] text-[#cbd5e1] leading-relaxed">
        {mod.blurb}
      </p>
    </div>
  </div>
);

const UpdatesPanel = ({ variant = "inline" }) => {
  const heightCap =
    variant === "inline"
      ? "max-h-[560px] overflow-y-auto"
      : "max-h-none";

  return (
    <section
      data-testid="updates-panel"
      className="border border-white/[0.08] bg-white/[0.02] w-full"
    >
      <div className="flex items-center justify-between border-b border-white/[0.08] px-4 sm:px-5 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <Activity size={12} className="text-[#FF0033] chaos-pulse" />
          <span className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.24em] text-[#cbd5e1] truncate">
            [ Operator Changelog / Updates ]
          </span>
        </div>
        <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#94a3b8] shrink-0">
          LIVE
        </span>
      </div>

      <div className={heightCap}>
        <div className="px-4 sm:px-5 py-2.5 border-b border-white/[0.06] bg-white/[0.01] flex items-center gap-2">
          <Zap size={11} className="text-emerald-400" />
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-bold">
            Active Patches
          </span>
        </div>
        {ACTIVE_PATCHES.map((p) => (
          <PatchRow key={p.id} patch={p} />
        ))}

        <div className="px-4 sm:px-5 py-2.5 border-b border-t border-white/[0.06] bg-white/[0.01] flex items-center gap-2">
          <Sparkles size={11} className="text-amber-400" />
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300 font-bold">
            Upcoming Modules
          </span>
        </div>
        {UPCOMING_MODULES.map((m) => (
          <UpcomingRow key={m.id} mod={m} />
        ))}
      </div>
    </section>
  );
};

export default UpdatesPanel;
